import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { TranslateService } from '@ngx-translate/core';
import {
  CiscoPbxExport,
  CucAutoAttendantsReport,
  JobState,
  MigrationComplexityScore,
} from '../../../shared/models/generated/ziroModels';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MigrationsCucAutoAttendantsContext } from '../../services/cisco/migrations-cuc-auto-attendants.context';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { DateAgoPipe } from '../../../shared/pipes/date-ago.pipe';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { DatatableFilterService } from '../../../shared/tables/datatable/datatable-filter.service';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { DatatableMultiSelectFilterComponent } from '../../../shared/tables/datatable/datatable-filters/datatable-multi-select-filter/datatable-multi-select-filter.component';
import { MigrationsAssessmentsContext } from '../../services/migrations-assessments.context';

interface AutoAttendantsDatatableRow extends DatatableRow {
  name: string;
  complexity: string;
  type: string;
  number: string;
}

@Component({
  selector: 'ziro-migrations-auto-attendants',
  standalone: false,
  templateUrl: './migrations-auto-attendants.component.html',
  styleUrls: ['../../migrations.component.scss'],
})
export class MigrationsAutoAttendantsComponent implements OnInit, OnDestroy {
  @ViewChild('complexityFilter') complexityFilter: DatatableMultiSelectFilterComponent;

  private _translateService = inject(TranslateService);
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _dateAgoPipe = inject(DateAgoPipe);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _migrationsCucAutoAttendantsContext = inject(MigrationsCucAutoAttendantsContext);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _datatableFilterService = inject(DatatableFilterService);
  private telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _changeDetectorRef = inject(ChangeDetectorRef);

  private readonly _complexityMap = new Map([
    [MigrationComplexityScore.SIMPLE, 'tkey;migrations.cisco.auto_attendants_report.complexity.simple'],
    [MigrationComplexityScore.COMPLEX, 'tkey;migrations.cisco.auto_attendants_report.complexity.complex'],
    [
      MigrationComplexityScore.REQUIRE_INVESTIGATION,
      'tkey;migrations.cisco.auto_attendants_report.complexity.require_investigation',
    ],
  ]);

  isLoading = true;
  showCustomAlert: boolean = false;

  tableDataAutomation = 'migrations-cisco-auto-attendants-datatable';

  typeFilterOptions: string[] = [
    this._translateService.instant('tkey;migrations.cisco.auto_attendants_report.type.call_handler'),
    this._translateService.instant('tkey;migrations.cisco.auto_attendants_report.type.directory_handler'),
    this._translateService.instant('tkey;migrations.cisco.auto_attendants_report.type.interview_handler'),
  ];

  typeMatchFn = (selectedValues: string[], row: AutoAttendantsDatatableRow): boolean =>
    this._datatableFilterService.filterMultiSelectValues(selectedValues, row.type);

  complexityFilterOptions: string[] = [
    this._translateService.instant('tkey;migrations.cisco.auto_attendants_report.complexity.simple'),
    this._translateService.instant('tkey;migrations.cisco.auto_attendants_report.complexity.complex'),
    this._translateService.instant('tkey;migrations.cisco.auto_attendants_report.complexity.require_investigation'),
  ];

  complexityMatchFn = (selectedValues: string[], row: AutoAttendantsDatatableRow): boolean =>
    this._datatableFilterService.filterMultiSelectValues(selectedValues, row.complexity);

  columns: DatatableColumn<AutoAttendantsDatatableRow>[] = [
    {
      name: 'name',
      label: 'tkey;migrations.cisco.auto_attendants.table.name',
      columnCharacterWidth: 40,
    },
    {
      name: 'number',
      label: 'tkey;migrations.cisco.auto_attendants.table.number',
      pipe: this.telephoneNumberFilter,
      columnCharacterWidth: 20,
    },
    {
      name: 'complexity',
      label: 'tkey;migrations.cisco.auto_attendants.table.complexity',
      columnCharacterWidth: 20,
    },
    {
      name: 'type',
      label: 'tkey;migrations.cisco.auto_attendants.table.type',
      columnCharacterWidth: 20,
    },
  ];
  tableRows: AutoAttendantsDatatableRow[] = [];
  assessmentId: string;

  private _tenantId: string;
  private _subscriptions = new Subscription();
  private _filter: string;

  ngOnInit(): void {
    this._initBottomNav();

    this.assessmentId = this._route.snapshot.params['id'];
    this._tenantId = this._route.snapshot.params['tenantId'];
    this._filter = this._route.snapshot.paramMap.get('filter');

    const exportSub = this._migrationsAssessmentsContext.state$.subscribe((assessments) => {
      const assessment = assessments.ciscoPbxExports.find(
        (assessment: CiscoPbxExport) => assessment.id === this.assessmentId,
      );
      this._initBreadcrumbs(assessment.name);
    });
    this._subscriptions.add(exportSub);

    const autoAttendantsSub = this._migrationsCucAutoAttendantsContext.state$.subscribe((report) => {
      this.tableRows = this._mapReportToRows(report);
      this.isLoading = false;
      this.showCustomAlert = false;
    });
    this._subscriptions.add(autoAttendantsSub);

    const autoAttendantsStatusSub = this._migrationsCucAutoAttendantsContext.jobState$.subscribe((status) => {
      if (status.lastRunTime) {
        this._updateBottomNavTimestamp(status.lastRunTime);
      }

      if (status.error && status.jobState === JobState.IDLE) {
        this.showCustomAlert = true;
        this.isLoading = false;
      }

      const isRefreshing = !status.lastRunTime || status.jobState !== JobState.IDLE;
      this._setRefreshButtonPendingState(isRefreshing);
    });
    this._subscriptions.add(autoAttendantsStatusSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: null,
      }),
    );
  }

  onFilterInitialized(): void {
    if (!this._filter) {
      return;
    }
    setTimeout(() => {
      const filter = this._translateComplexityFilterToEnum(
        decodeURIComponent(this._filter),
      ) as MigrationComplexityScore;

      if (!this.complexityFilter?.selectedItems.length && !!filter) {
        this.complexityFilter?.onFilterChange(this._getComplexityTranslation(filter));
        this._changeDetectorRef.detectChanges();
      }
    });
  }

  private _mapReportToRows(report: CucAutoAttendantsReport): AutoAttendantsDatatableRow[] {
    const callHandlerRows: AutoAttendantsDatatableRow[] = (report.callHandlers ?? []).map((callHandler) => {
      const isReportWithoutUrlFilter = window.location.href.endsWith('cuc-auto-attendants-report');
      const baseUrl = isReportWithoutUrlFilter
        ? window.location.href
        : window.location.href.substring(0, window.location.href.lastIndexOf('/'));
      return {
        name: callHandler.displayName || '',
        number: callHandler.extension || '',
        complexity: this._getComplexityTranslation(callHandler.migrationComplexityScore),
        type: this._translateService.instant('tkey;migrations.cisco.auto_attendants_report.type.call_handler'),
        hardcodedTooltips: {
          type: this._translateService.instant(
            'tkey;migrations.cisco.auto_attendants_report.type.call_handler.tooltip',
          ),
        },
        actions: [
          {
            dataAutomation: 'detailed-call-handler-button',
            type: ButtonTypes.LINK,
            style: ButtonStyles.PRIMARY,
            icon: ZiroIcons.NEXT,
            disabled: false,
            href: `${baseUrl}/call-handler?subchart=${callHandler.id}`,
          },
        ],
      };
    });

    const directoryHandlerRows: AutoAttendantsDatatableRow[] = (report.directoryHandlers ?? []).map(
      (directoryHandler) => ({
        name: directoryHandler.displayName || '',
        number: directoryHandler.extension || '',
        complexity: this._getComplexityTranslation(directoryHandler.migrationComplexityScore),
        type: this._translateService.instant('tkey;migrations.cisco.auto_attendants_report.type.directory_handler'),
        hardcodedTooltips: {
          type: this._translateService.instant(
            'tkey;migrations.cisco.auto_attendants_report.type.directory_handler.tooltip',
          ),
        },
        actions: [
          {
            dataAutomation: 'detailed-call-handler-button',
            type: ButtonTypes.LINK,
            style: ButtonStyles.PRIMARY,
            icon: ZiroIcons.NEXT,
            disabled: true,
            disabledTooltip: this._translateService.instant(
              'tkey;migrations.cisco.auto_attendants_report.type.directory_handler.action.disabled',
            ),
            href: '',
          },
        ],
      }),
    );

    const interviewHandlerRows: AutoAttendantsDatatableRow[] = (report.interviewHandlers ?? []).map(
      (interviewHandler) => ({
        name: interviewHandler.displayName || '',
        number: interviewHandler.extension || '',
        complexity: this._getComplexityTranslation(interviewHandler.migrationComplexityScore),
        type: this._translateService.instant('tkey;migrations.cisco.auto_attendants_report.type.interview_handler'),
        hardcodedTooltips: {
          type: this._translateService.instant(
            'tkey;migrations.cisco.auto_attendants_report.type.interview_handler.tooltip',
          ),
        },
        actions: [
          {
            dataAutomation: 'detailed-call-handler-button',
            type: ButtonTypes.LINK,
            style: ButtonStyles.PRIMARY,
            icon: ZiroIcons.NEXT,
            disabled: true,
            disabledTooltip: this._translateService.instant(
              'tkey;migrations.cisco.auto_attendants_report.type.interview_handler.action.disabled',
            ),
            href: '',
          },
        ],
      }),
    );

    return [...callHandlerRows, ...directoryHandlerRows, ...interviewHandlerRows].sort(
      (a, b) => a.type.localeCompare(b.type) || +!!b.number - +!!a.number || a.name.localeCompare(b.name),
    );
  }

  onRefreshClick(): void {
    this.isLoading = true;
    this.showCustomAlert = false;
    this._migrationsCucAutoAttendantsContext.refresh().subscribe();
  }

  private _initBreadcrumbs(migrationName: string): void {
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this._tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: migrationName,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/cisco/${this.assessmentId}`,
      },
      { label: 'tkey;migrations.cisco.auto_attendants_report.table.auto_attendants' },
    ]);
  }

  private _updateBottomNavTimestamp(timestamp: string): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: `
              <i class="${ZiroIcons.TIMESTAMP}"></i>
              ${this._translateService.instant(
                'tkey;reports.cisco.auto_attendants_report.last_updated',
              )} <strong>${this._dateAgoPipe.transform(timestamp)}</strong>
            `,
      }),
    );
  }

  private _setRefreshButtonPendingState(state: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'cuc-auto-attendants-report-refresh',
        state: {
          pending: state,
          buttonDisableState: { disabled: state, tooltipKey: '' },
        },
      }),
    );
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'cuc-auto-attendants-back-button',
          dataAutomation: 'migrations-cisco-auto-attendants-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => {
            this._router.navigate(['..'], { relativeTo: this._route });
          },
        },
        {
          id: 'cuc-auto-attendants-report-refresh',
          dataAutomation: 'migrations-cisco-auto-attendants-refresh-button',
          label: 'tkey;migrations.cisco.refresh_button.label',
          icon: ZiroIcons.REFRESH,
          buttonClass: ButtonStyles.PRIMARY,
          state: {
            pending: false,
            buttonDisableState: { disabled: false },
          },
          cb: () => this.onRefreshClick(),
        },
      ]),
    );
  }

  private _getComplexityTranslation(complexityScore: MigrationComplexityScore): string {
    const translationKey = this._complexityMap.get(complexityScore);
    return translationKey ? this._translateService.instant(translationKey) : String(complexityScore);
  }

  private _translateComplexityFilterToEnum(translatedFilter: string): string {
    for (const [enumValue, translationKey] of this._complexityMap.entries()) {
      if (this._translateService.instant(translationKey) === translatedFilter) {
        return enumValue;
      }
    }
    return null;
  }
}
